import React, { useEffect, useMemo, useRef, useState } from "react";
import ReceivedMessage from "@src/components/auctionAgent/chatting/chattingChannel/ReceivedMessage";
import SentMessage from "@src/components/auctionAgent/chatting/chattingChannel/SentMessage";
import styled from "styled-components";
import { useSendBirdChat } from "@src/contexts/SendBirdChatContext";
import { UserMessage } from "@sendbird/chat/message";

import { format } from "date-fns";
import { useGetChattingRoomInfo } from "@src/hooks/auctionAgent/chat";
import useInfiniteScroll from "react-infinite-scroll-hook";

const ChattingArea = ({ chattingRoomNo, userId }: { chattingRoomNo: number; userId: string }) => {
    const { data: chattingRoomInfo } = useGetChattingRoomInfo(chattingRoomNo, true);
    const { sendBirdMessages, readStatusMap, msgMessages, setRequestedMorePrevMsgs, requestedMorePrevMsgs } = useSendBirdChat();
    const chatRef = useRef<HTMLDivElement>(null);
    const prevScrollHeight = useRef<number | null>(null);

    const [scrollRef] = useInfiniteScroll({
        loading: false,
        hasNextPage: !!requestedMorePrevMsgs.hasNextPage,
        onLoadMore: () => {
            setRequestedMorePrevMsgs((prev) => ({ ...prev, requested: true }));
        },
    });

    // ✅ 메시지 병합 후 정렬
    const allMessages = useMemo(() => {
        const sendbirdMapped = sendBirdMessages.map((m) => ({
            type: "sendbird",
            id: m.messageId.toString(),
            isMine: m.sender?.userId === userId,
            message: (m as UserMessage).message,
            createdAt: m.createdAt,
            senderNo: Number(m.sender?.userId),
            isRead: false,
            raw: m,
        }));

        const dbMapped = msgMessages.map((m) => ({
            type: "db",
            id: m.sendbirdMessageId.toString(),
            isMine: m.isMine,
            message: m.message,
            createdAt: new Date(m.createdAt).getTime(),
            senderNo: m.senderNo,
            isRead: m.isRead,
        }));

        // ✅ msgMessages가 우선되도록: sendbird 먼저 넣고, db로 덮어쓰기
        const map = new Map<string, typeof dbMapped[number]>();
        sendbirdMapped.forEach((msg) => map.set(msg.id, msg));
        dbMapped.forEach((msg) => map.set(msg.id, msg)); // 덮어씀

        const deduplicated = Array.from(map.values());

        return deduplicated.sort((a, b) => a.createdAt - b.createdAt);
    }, [msgMessages, sendBirdMessages]);

    // ✅ 날짜별 그룹화
    const groupedByDate = useMemo(() => {
        return allMessages.reduce<Record<string, typeof allMessages>>((acc, msg) => {
            const dateKey = format(new Date(msg.createdAt), "yyyy-MM-dd");
            if (!acc[dateKey]) acc[dateKey] = [];
            acc[dateKey].push(msg);
            return acc;
        }, {});
    }, [allMessages]);

    const handleResize = () => {
        requestAnimationFrame(() => {
            if (chatRef.current) {
                chatRef.current.scrollTop = chatRef.current.scrollHeight;
            }
        });
    };
    useEffect(() => {
        handleResize();
    }, [chatRef.current]);

    // 2. 이전 메시지 불러오기 전: 스크롤 높이 저장
    useEffect(() => {
        if (requestedMorePrevMsgs.requested && chatRef.current) {
            prevScrollHeight.current = chatRef.current.scrollHeight;
        }
    }, [requestedMorePrevMsgs.requested]);

    // 3. 이전 메시지 불러온 후: 스크롤 위치 보정
    useEffect(() => {
        if (requestedMorePrevMsgs.requested && chatRef.current && prevScrollHeight.current !== null) {
            const diff = chatRef.current.scrollHeight - prevScrollHeight.current;
            chatRef.current.scrollTop += diff;
            prevScrollHeight.current = null;

            setRequestedMorePrevMsgs((prev) => ({ ...prev, requested: false }));
        }
    }, [msgMessages.length]);

    // 4. Sendbird 메시지 새로 오면 → 아래로
    useEffect(() => {
        if (sendBirdMessages.length === 0) return;
        if (!requestedMorePrevMsgs.requested && chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    }, [sendBirdMessages.length]);

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <ChattingAreaBox ref={chatRef}>
            {requestedMorePrevMsgs.hasNextPage && <div className="load-more-messages" ref={scrollRef} />}
            {Object.entries(groupedByDate).map(([date, messages]) => (
                <div key={date}>
                    <DateDivider>{format(new Date(date), "yyyy년 M월 d일")}</DateDivider>
                    {messages.map((msg, index) => {
                        const prev = messages[index - 1];

                        const isDifferentSender = !prev || prev.senderNo !== msg.senderNo;

                        if (msg.isMine) {
                            return (
                                <SentMessage
                                    key={msg.id}
                                    message={msg.message}
                                    createdAt={new Date(msg.createdAt).toISOString()}
                                    showReadStatus={msg.type === "sendbird" || (msg.type === "db" && !msg.isRead)}
                                    isReadByOpponent={readStatusMap && Object.values(readStatusMap)[0]?.readAt >= msg.createdAt}
                                />
                            );
                        } else {
                            return (
                                <ReceivedMessage
                                    key={msg.id}
                                    message={msg.message}
                                    createdAt={new Date(msg.createdAt).toISOString()}
                                    isDifferentSender={isDifferentSender}
                                    senderImage={chattingRoomInfo?.otherUser?.profileImage ?? ""}
                                />
                            );
                        }
                    })}
                </div>
            ))}
        </ChattingAreaBox>
    );
};

export default ChattingArea;

// ✅ 스타일
const ChattingAreaBox = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
        display: none;
    }

    .load-more-messages {
        width: 100%;
        text-align: center;
        min-height: 20px;
    }
`;

const DateDivider = styled.div`
    text-align: center;
    margin: 12px 0;
    color: #888;
    font-size: 13px;
    margin: 24px 0;
`;
